import { useCallback, useEffect, useState } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const iosUA = () => /iphone|ipad|ipod/i.test(navigator.userAgent)
const standalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as { standalone?: boolean }).standalone === true

/**
 * Wraps the browser's native "Add to Home Screen" install flow.
 *
 * Chrome/Edge/Android fire `beforeinstallprompt`; we stash the event (and
 * call preventDefault() so the browser's own mini-infobar doesn't also show)
 * so `promptInstall()` can trigger it later from a user gesture (a button
 * click in InstallCard/InstallPage). iOS Safari never fires that event —
 * there is no programmatic prompt there, so `isIos` signals the UI to show
 * the manual Share -> Add to Home Screen guide instead.
 */
export function useInstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(standalone())

  useEffect(() => {
    const onPrompt = (e: Event) => { e.preventDefault(); setDeferred(e as BeforeInstallPromptEvent) }
    const onInstalled = () => { setIsInstalled(true); setDeferred(null) }
    window.addEventListener('beforeinstallprompt', onPrompt)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferred) return
    await deferred.prompt()
    await deferred.userChoice
    setDeferred(null)
  }, [deferred])

  return { canPrompt: deferred !== null, isInstalled, isIos: iosUA() && !standalone(), promptInstall }
}
